import { createProfessionalExporter } from '../../../utils/professionalExport';
import { formatCurrency } from '../receptionistData';
import { saleRowEndDate, saleRowStartDate, saleRowDuration, saleRowPackageName } from './salePeriodUtils';
import { saleRowLeft, saleRowPaid, saleRowTotal } from './salePaymentUtils';
import { PAYMENT_METHOD_OPTIONS, splitAmountForMethod } from '../../../utils/splitPaymentUtils';

function slugify(value) {
  return String(value || 'sales')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '') || 'sales';
}

/** CR. No. = invoice_id on the payment row. */
function saleReceiptNumber(row) {
  const invoice = String(row?.invoiceId || '').trim();
  if (invoice && invoice !== '—') return invoice;
  const paymentRef = String(row?.paymentId || row?.id || '').trim();
  if (paymentRef && paymentRef !== '—' && !/^[0-9a-f-]{36}$/i.test(paymentRef)) {
    return paymentRef;
  }
  return '—';
}

/** Build print/export helpers for a sale history table. */
export function createSaleHistoryExporter({
  title = 'Sales',
  itemLabel = 'Item',
  slug,
  getItemValue = saleRowPackageName,
} = {}) {
  const methodHeaders = PAYMENT_METHOD_OPTIONS.map((m) => m.label);
  const headers = [
    'Date',
    'CR. No.',
    'Serial No',
    'Member',
    itemLabel,
    'Duration',
    ...methodHeaders,
    'Total',
    'Total Paid',
    'Total Left',
    'Start Date',
    'End Date',
    'Note',
  ];

  const exporter = createProfessionalExporter({
    title,
    headers,
    slug: slugify(slug || title),
    getRows: (list) =>
      (list || []).map((row) => [
        String(row.date || '').slice(0, 10) || '—',
        saleReceiptNumber(row),
        row.gymId || row.serialNo || '—',
        row.member || '—',
        getItemValue(row),
        saleRowDuration(row),
        ...PAYMENT_METHOD_OPTIONS.map(({ key }) => {
          const amt = splitAmountForMethod(row, key);
          return amt > 0 ? formatCurrency(amt) : '—';
        }),
        formatCurrency(saleRowTotal(row)),
        formatCurrency(saleRowPaid(row)),
        formatCurrency(saleRowLeft(row)),
        saleRowStartDate(row),
        saleRowEndDate(row),
        row.paymentNote || '—',
      ]),
    getSummary: (list) => {
      const paid = (list || []).reduce((sum, row) => sum + saleRowPaid(row), 0);
      return [
        `Total sales: ${(list || []).length}`,
        `Total collected: ${formatCurrency(paid)}`,
      ];
    },
  });

  return {
    print: (data) => exporter.print(data),
    csv: (data) => exporter.csv(data),
    pdf: (data) => exporter.pdf(data),
    excel: (data) => exporter.excel(data),
    exportAll: (data) => exporter.export(data),
  };
}
